import { DOCUMENT, Injectable, InjectionToken, Provider, effect, inject, signal } from '@angular/core';
import { isServer } from '../utilities/platform-type';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Consent status for Google Consent Mode v2
 */
export type ConsentState = 'granted' | 'denied';

/**
 * GTM Consent parameters state
 */
export interface GTMConsentState {
  ad_storage: ConsentState;
  ad_user_data: ConsentState;
  ad_personalization: ConsentState;

  analytics_storage: ConsentState;
  personalization_storage: ConsentState;

  functionality_storage: ConsentState;
  security_storage: ConsentState;
}

export const GTM_CONTAINER_ID = new InjectionToken<string>('GTM_CONTAINER_ID');

@Injectable({
  providedIn: 'root'
})
export class GTMService {
  private readonly document = inject(DOCUMENT);
  private readonly gtmId = inject(GTM_CONTAINER_ID);

  /**
   * Signal tracking current consent state
   * Exposed as readonly for components to observe
   */
  readonly consent$ = signal<GTMConsentState>({
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',

    analytics_storage: 'denied',
    personalization_storage: 'denied',

    functionality_storage: 'granted',
    security_storage: 'granted',
  });

  /**
   * Initializes Google Tag Manager with Advanced Consent Mode.
   * Sets ALL consent parameters to 'denied' by default, then loads GTM.
   * Uses wait_for_update to allow consent updates before tags fire.
   * Only runs in browser context, not during SSR.
   * Safe to call multiple times - will only initialize once.
   */
  constructor() {
    if (isServer())
      return;

    window.dataLayer = window.dataLayer || [];

    const consent = this.consent$();

    // Set default consent to 'denied' for all parameters (Advanced Consent Mode)
    gtag('consent', 'default', {
      ...consent,
      wait_for_update: 500 // Wait 500ms for consent update before firing tags
    });

    {
      effect(() => {
        const value = this.consent$();
        gtag('consent', 'update', value);

      });
    }



    // Now inject GTM script
    this.injectGTMScript();
  }

  /**
   * Injects GTM script following official Google Tag Manager implementation
   * Matches the official snippet: (function(w,d,s,l,i){...})(window,document,'script','dataLayer','GTM-XXX');
   */
  private injectGTMScript(): void {
    const dataLayerName = 'dataLayer';

    // Push GTM start event (official implementation requirement)
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    // Create script element
    const script = this.document.createElement('script');
    script.async = true;

    // Add dataLayer name parameter if not default (matches official snippet)
    const dataLayerParam = dataLayerName !== 'dataLayer' ? `&l=${dataLayerName}` : '';
    script.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmId}${dataLayerParam}`;

    // Insert before first script tag (matches official GTM snippet)
    const firstScript = this.document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      // Fallback: append to head if no scripts exist yet
      this.document.head.appendChild(script);
    }
  }


}


export function provideGTM(containerId: string): Provider {
  return [
    {
      provide: GTM_CONTAINER_ID,
      useValue: containerId
    },
    GTMService
  ];
}


// eslint-disable-next-line prefer-rest-params, @typescript-eslint/no-unused-vars
function gtag(...args: unknown[]) { window.dataLayer.push(arguments); }